/**
 * CSV learner import, sized for a dialog.
 *
 * Used inside the course-invitation "Add learners" modal: the trainer drops a
 * CSV, the rows are validated locally (same parser as the standalone Bulk
 * Upload page), and on import every person from the file — freshly created
 * ones AND people who already belonged to the workspace — is handed back so
 * the caller can drop them straight into the audience being built.
 */
import React, { useCallback, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowDownTrayIcon, DocumentTextIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useBulkImportLearners } from '@/services/learners';
import { checkFile, downloadTemplate, parseLearnersCsv, type ParsedFile } from '@/utils/learnerCsv';

interface Props {
  /** Receives every learner id from the file that is now in the workspace. */
  onImported: (learnerIds: string[]) => void;
  onBack: () => void;
}

const CsvLearnerImport: React.FC<Props> = ({ onImported, onBack }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const bulkImport = useBulkImportLearners();

  const takeFile = useCallback(async (file: File | undefined) => {
    if (!file) return;
    const problem = checkFile(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    try {
      const result = await parseLearnersCsv(file);
      setParsed(result);
      if (result.valid.length === 0) {
        toast.error('No valid rows found — check the column names match the template.');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not read the file.');
    }
  }, []);

  const submit = async () => {
    if (!parsed || parsed.valid.length === 0 || bulkImport.isPending) return;
    try {
      const result = await bulkImport.mutateAsync({ learners: parsed.valid });
      // Newly created + already-present people all belong in the audience.
      const ids = [
        ...(result.imported_learner_ids ?? []),
        ...(result.existing_learner_ids ?? []),
      ];
      const already = result.existing_learner_ids?.length ?? 0;
      toast.success(
        already > 0
          ? `${result.successful} added to the workspace · ${already} already there — all selected.`
          : `${result.successful} learner${result.successful === 1 ? '' : 's'} added and selected.`
      );
      onImported(ids);
    } catch {
      toast.error('The upload failed. Nothing was saved — please try again.');
    }
  };

  const canSubmit = !!parsed && parsed.valid.length > 0 && !bulkImport.isPending;

  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="text-xs font-semibold text-text-secondary hover:text-text-primary focus-ring active:scale-[0.97] transition-[color,transform] duration-150 mb-3"
      >
        ← Back
      </button>
      <h2 className="text-xl font-bold text-text-primary text-center mb-1">Bulk Upload with CSV</h2>
      <p className="text-center text-sm text-text-secondary mb-4">
        Everyone in the file joins the workspace and is added to this invitation.
      </p>

      <button
        type="button"
        onClick={downloadTemplate}
        className="mx-auto mb-4 flex items-center gap-1.5 rounded-full border border-border-primary/40 px-3.5 py-1.5 text-xs font-bold text-text-primary hover:bg-text-primary/[0.06] focus-ring active:scale-[0.97] transition-[background-color,transform] duration-150"
      >
        <ArrowDownTrayIcon className="h-4 w-4" />
        Download template
      </button>

      {!parsed ? (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            void takeFile(e.dataTransfer.files[0]);
          }}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
          }}
          className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors focus-ring ${
            isDragging
              ? 'border-primary-500 bg-primary-500/[0.06]'
              : 'border-border-primary/40 hover:border-primary-500/60'
          }`}
        >
          <DocumentTextIcon className="h-8 w-8 text-text-tertiary" aria-hidden="true" />
          <p className="text-sm font-semibold text-text-primary">Drop your CSV here</p>
          <p className="text-xs text-text-secondary">or click to browse · max 1000 rows</p>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            aria-label="Choose a CSV file"
            onChange={(e) => {
              void takeFile(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </div>
      ) : (
        <div className="rounded-2xl border border-border-primary/20 px-4 py-3">
          <div className="flex items-center gap-3">
            <DocumentTextIcon className="h-5 w-5 shrink-0 text-primary-500" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-text-primary">{parsed.name}</p>
              <p className="text-xs text-text-secondary">
                {parsed.valid.length} ready to add
                {parsed.errors.length > 0 &&
                  ` · ${parsed.errors.length} row${parsed.errors.length === 1 ? '' : 's'} skipped`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setParsed(null)}
              aria-label="Remove file"
              className="rounded-full p-1 text-text-tertiary hover:bg-text-primary/[0.06] hover:text-text-primary focus-ring transition-colors"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          {parsed.errors.length > 0 && (
            <ul className="mt-3 max-h-[120px] space-y-1 overflow-y-auto border-t border-border-primary/15 pt-2">
              {parsed.errors.slice(0, 6).map((e) => (
                <li key={`${e.row}-${e.email}`} className="text-xs text-text-secondary">
                  Row {e.row}: {e.email || '(no email)'} — {e.error}
                </li>
              ))}
              {parsed.errors.length > 6 && (
                <li className="text-xs text-text-tertiary">
                  …and {parsed.errors.length - 6} more
                </li>
              )}
            </ul>
          )}
        </div>
      )}

      <div className="mt-5 flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          data-testid="csv-import-submit"
          className="inline-flex min-h-[36px] items-center justify-center gap-2 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 px-5 py-1.5 text-sm font-bold transition-opacity duration-150 hover:opacity-90 focus-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          {bulkImport.isPending ? 'Importing…' : 'Import & add'}
        </button>
      </div>
    </>
  );
};

export default CsvLearnerImport;
